<template>
  <span
    class="agent-mapped-avatar"
    :class="mappedIcon ? 'has-svg' : 'has-image'"
    :style="avatarStyle"
  >
    <svg-icon
      v-if="mappedIcon"
      :icon-class="mappedIcon"
    />
    <img
      v-else-if="resolvedSrc"
      :src="resolvedSrc"
      :alt="alt"
      loading="lazy"
      decoding="async"
    >
  </span>
</template>

<script>
import SvgIcon from '@/components/SvgIcon/index.vue'
import { getAgentMappedIcon, getAgentMappedIconStyle } from '@/utils/agentIcon'

export default {
  name: 'AgentMappedAvatar',
  components: { SvgIcon },
  props: {
    agent: {
      type: Object,
      default: null
    },
    src: {
      type: String,
      default: ''
    },
    alt: {
      type: String,
      default: ''
    },
    size: {
      type: [Number, String],
      default: 32
    },
    resize: {
      type: [Number, String],
      default: 64
    },
    optimize: {
      type: Boolean,
      default: true
    },
    preferImage: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    normalizedAgent() {
      const agent = this.agent || {}
      return {
        ...agent,
        agent_category: agent.agent_category || agent.conversation_type || '',
        agent_capability: agent.agent_capability || ''
      }
    },
    mappedIcon() {
      if (this.shouldPreferImage) {
        return ''
      }
      return getAgentMappedIcon(this.normalizedAgent)
    },
    shouldPreferImage() {
      return this.preferImage &&
        this.imageSrc &&
        this.normalizedAgent.agent_category === 'chat_agent' &&
        this.normalizedAgent.agent_capability === 'text'
    },
    avatarStyle() {
      return {
        ...getAgentMappedIconStyle(this.normalizedAgent),
        '--agent-avatar-size': this.sizeValue,
        '--agent-avatar-icon-size': this.iconSizeValue
      }
    },
    sizeValue() {
      const size = String(this.size || 32)
      return /^\d+$/.test(size) ? `${size}px` : size
    },
    iconSizeValue() {
      const size = Number(this.size)
      return Number.isFinite(size) ? `${Math.round(size * 0.9)}px` : '90%'
    },
    imageSrc() {
      return this.src || this.normalizedAgent.avatar || ''
    },
    resolvedSrc() {
      if (!this.imageSrc || !this.optimize) return this.imageSrc
      if (/^(data:|blob:)/.test(this.imageSrc)) return this.imageSrc
      if (this.imageSrc.includes('x-oss-process=')) return this.imageSrc

      const joiner = this.imageSrc.includes('?') ? '&' : '?'
      return `${this.imageSrc}${joiner}x-oss-process=image/resize,w_${this.resize},h_${this.resize}`
    }
  }
}
</script>

<style lang="scss" scoped>
.agent-mapped-avatar {
  width: var(--agent-avatar-size);
  height: var(--agent-avatar-size);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 var(--agent-avatar-size);
  color: var(--agent-icon-color, var(--agst-text-color-primary, #1f2937));

  &.has-svg {
    overflow: visible;

    .svg-icon {
      width: var(--agent-avatar-icon-size);
      height: var(--agent-avatar-icon-size);
    }
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: var(--agst-border-radius-base, 4px);
  }
}
</style>
